import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  X, Loader2, TrendingUp, TrendingDown, Wallet,
  Gift, Sparkles, Users, Zap, Image as ImageIcon, Download,
} from "lucide-react";
import { adsApiCall } from "./api";
import { toast } from "sonner";

const REASON_META = {
  referral_signup:   { label: "Referral — signup",         icon: Gift,       color: "#a855f7" },
  referral_welcome:  { label: "Welcome bonus",             icon: Gift,       color: "#a855f7" },
  remix_earned:      { label: "Remix royalty",             icon: Sparkles,   color: "#7c3aed" },
  instant_campaign:  { label: "Instant Campaign — 25 ads", icon: Zap,        color: "#0ea5e9" },
  ai_image:          { label: "AI image generation",       icon: ImageIcon,  color: "#f59e0b" },
  grant:             { label: "Credit grant",              icon: Wallet,     color: "#22c55e" },
};
function metaFor(reason) {
  if (reason && reason.startsWith("export_")) {
    return { label: `Export ${reason.replace("export_", ".").toUpperCase()}`, icon: Download, color: "#6b7280" };
  }
  return REASON_META[reason] || { label: reason || "Activity", icon: Wallet, color: "#6b7280" };
}

function relTime(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 30 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function CreditsLedger({ onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    adsApiCall.creditsLedger().then(setData).catch(() => {
      toast.error("Couldn't load ledger");
      onClose();
    });
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="cp-modal-backdrop" onClick={onClose} data-testid="credits-ledger-modal">
      <div className="cp-modal cl-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ads-mobile-sheet-close" style={{ display: "flex" }} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className="cp-modal-title">Credits ledger</div>
        <div className="cp-modal-sub">Every credit earned and spent — full transparency.</div>
        {!data ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : (
          <>
            <div className="cl-summary" data-testid="credits-ledger-summary">
              <div className="cl-stat cl-earned">
                <TrendingUp size={14} />
                <div>
                  <div className="cl-stat-label">Earned</div>
                  <div className="cl-stat-value">+{data.earned}</div>
                </div>
              </div>
              <div className="cl-stat cl-spent">
                <TrendingDown size={14} />
                <div>
                  <div className="cl-stat-label">Spent</div>
                  <div className="cl-stat-value">−{data.spent}</div>
                </div>
              </div>
              <div className="cl-stat cl-balance">
                <Wallet size={14} />
                <div>
                  <div className="cl-stat-label">Balance</div>
                  <div className="cl-stat-value">{data.balance}</div>
                </div>
              </div>
            </div>

            {data.rows.length === 0 ? (
              <div className="ads-empty-state">No activity yet. Run your first Instant Campaign to get started.</div>
            ) : (
              <div className="cl-list" data-testid="credits-ledger-list">
                {data.rows.map((r, i) => {
                  const m = metaFor(r.reason);
                  const Icon = m.icon;
                  const campaignId = r.meta?.campaign_id || r.meta?.origin_campaign_id || r.meta?.remix_campaign_id;
                  return (
                    <div key={i} className="cl-row" data-testid={`credits-ledger-row-${i}`}>
                      <div className="cl-row-icon" style={{ background: m.color + "26", color: m.color }}>
                        <Icon size={14} />
                      </div>
                      <div className="cl-row-body">
                        <div className="cl-row-label">{m.label}</div>
                        <div className="cl-row-meta">
                          {r.meta?.referred_email && <span>{r.meta.referred_email}</span>}
                          {r.meta?.remixer_email && <span>{r.meta.remixer_email}</span>}
                          {r.meta?.referrer_email && <span>via {r.meta.referrer_email}</span>}
                          {r.meta?.prompt && <span>"{r.meta.prompt.slice(0, 64)}…"</span>}
                          {campaignId && (
                            <Link to={`/ads-creator/my-ads`} onClick={onClose} className="cl-row-link">
                              {campaignId.slice(0, 14)}
                            </Link>
                          )}
                          <span className="cl-row-time">· {relTime(r.ts)}</span>
                        </div>
                      </div>
                      <div className={`cl-row-delta ${r.delta > 0 ? "pos" : "neg"}`}>
                        {r.delta > 0 ? "+" : ""}{r.delta}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
